import { useEffect, useRef, useState } from "react";
import { api, resolveImage } from "../lib/api";
import { toast } from "sonner";
import { Upload, Trash2, Check, X, ImagePlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";

export default function ImagePicker({
  open,
  onOpenChange,
  multiple = false,
  initialSelected = [],
  onConfirm,
}) {
  const [images, setImages] = useState([]);
  const [selected, setSelected] = useState(new Set(initialSelected));
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setSelected(new Set(initialSelected));
      load();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/images");
      setImages(res.data);
    } catch {
      toast.error("No se pudo cargar la biblioteca");
    } finally {
      setLoading(false);
    }
  };

  const toggle = (url) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (multiple) {
        if (next.has(url)) next.delete(url);
        else next.add(url);
      } else {
        next.clear();
        next.add(url);
      }
      return next;
    });
  };

  const handleFiles = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const res = await api.post("/admin/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        toast.success(`Subida: ${res.data.filename}`);
      }
      await load();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Error al subir";
      toast.error(msg);
    } finally {
      setUploading(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    handleFiles(Array.from(e.dataTransfer.files || []));
  };

  const removeImage = async (fname) => {
    if (!window.confirm(`¿Eliminar ${fname}?`)) return;
    try {
      await api.delete(`/admin/images/${fname}`);
      toast.success("Imagen eliminada");
      setSelected((prev) => {
        const next = new Set(prev);
        next.delete(`/api/uploads/${fname}`);
        return next;
      });
      load();
    } catch {
      toast.error("No se pudo eliminar");
    }
  };

  const confirm = () => {
    const arr = Array.from(selected);
    onConfirm(multiple ? arr : arr[0] || "");
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col bg-[var(--c-bg-deep)] border-[var(--c-bio-green)]/25 text-[var(--c-text)]">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl text-[var(--c-text)]">
            Biblioteca de imágenes
          </DialogTitle>
          <DialogDescription className="text-[var(--c-text)]/65">
            {multiple
              ? "Selecciona una o varias imágenes para la galería."
              : "Selecciona una imagen o sube una nueva."}
          </DialogDescription>
        </DialogHeader>

        <div
          onDragOver={(e) => e.preventDefault()}
          onDrop={onDrop}
          className="mt-2 rounded-xl border-2 border-dashed border-[var(--c-firefly-gold)]/50 bg-[var(--c-bg-forest)]/60 p-5 flex items-center justify-between gap-4"
        >
          <div className="flex items-center gap-3 text-sm text-[var(--c-text)]/80">
            <Upload className="w-5 h-5 text-[var(--c-firefly-gold)]" />
            Arrastra imágenes aquí o selecciónalas desde tu equipo (png, jpg, webp · máx 8 MB)
          </div>
          <div>
            <input
              ref={inputRef}
              type="file"
              accept="image/png,image/jpeg,image/webp,image/gif"
              multiple
              className="hidden"
              data-testid="image-upload-input"
              onChange={(e) => handleFiles(Array.from(e.target.files || []))}
            />
            <Button
              type="button"
              disabled={uploading}
              onClick={() => inputRef.current?.click()}
              data-testid="image-upload-btn"
              className="bg-gradient-to-r from-[var(--c-firefly-gold)] to-[var(--c-bio-green)] hover:opacity-90 text-[#0B1A0F] rounded-full font-semibold"
            >
              <ImagePlus className="w-4 h-4 mr-1" />
              {uploading ? "Subiendo..." : "Subir"}
            </Button>
          </div>
        </div>

        <div className="mt-4 flex-1 overflow-y-auto pr-1">
          {loading && (
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
              {Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className="aspect-square rounded-xl bg-[var(--c-bg-forest)]/70 animate-pulse" />
              ))}
            </div>
          )}
          {!loading && images.length === 0 && (
            <div className="text-center py-10 text-[var(--c-text)]/55 text-sm">
              Aún no hay imágenes. Sube la primera.
            </div>
          )}
          {!loading && images.length > 0 && (
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
              {images.map((img) => {
                const isSel = selected.has(img.url);
                return (
                  <div
                    key={img.filename}
                    className={`relative group rounded-xl overflow-hidden border-2 transition-all cursor-pointer ${
                      isSel
                        ? "border-[var(--c-firefly-gold)] ring-2 ring-[var(--c-firefly-gold)]/40"
                        : "border-[var(--c-bio-green)]/15"
                    }`}
                    onClick={() => toggle(img.url)}
                    data-testid={`image-tile-${img.filename}`}
                  >
                    <img
                      src={resolveImage(img.url)}
                      alt={img.filename}
                      className="w-full aspect-square object-cover"
                    />
                    {isSel && (
                      <div className="absolute top-2 left-2 w-6 h-6 rounded-full bg-[var(--c-firefly-gold)] text-[#0B1A0F] flex items-center justify-center">
                        <Check className="w-4 h-4" />
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        removeImage(img.filename);
                      }}
                      className="absolute top-2 right-2 w-7 h-7 rounded-full bg-black/70 text-white opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                      aria-label="Eliminar"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <DialogFooter className="mt-3 flex items-center justify-between">
          <span className="text-xs text-[var(--c-text)]/55">
            {selected.size} {multiple ? "seleccionada(s)" : "elegida"}
          </span>
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="bg-transparent border-[var(--c-bio-green)]/30 text-[var(--c-text)] hover:bg-[var(--c-bg-forest)]"
            >
              <X className="w-4 h-4 mr-1" /> Cancelar
            </Button>
            <Button
              type="button"
              onClick={confirm}
              disabled={selected.size === 0}
              data-testid="image-picker-confirm"
              className="bg-gradient-to-r from-[var(--c-firefly-gold)] to-[var(--c-bio-green)] hover:opacity-90 text-[#0B1A0F] font-semibold"
            >
              <Check className="w-4 h-4 mr-1" /> Usar selección
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
